/**
 * La carte corporelle d'une routine — **où va le travail**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 LE MODÈLE EST REPRIS, PLUS DESSINÉ *(06/09/2026)*
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demandé après usage : « utilise un schéma que tu peux trouver sur internet
 * qui est plus réaliste, je trouve que ça ne rend pas bien pour le moment ».
 *
 * Les deux versions précédentes étaient **dessinées à la main** — d'abord par
 * les seules régions musculaires (29/08), puis avec une silhouette de base
 * dessous (le même jour, « plus proche d'un corps humain et plus détaillée »).
 * Le second essai était déjà meilleur que le premier, et restait ce qu'il
 * annonçait : « un diagramme stylisé, pas une planche d'anatomie ». À l'usage,
 * ce n'était pas assez.
 *
 * ⚠️ **La géométrie vient donc de `bodyModel.ts`**, repris de
 * `react-body-highlighter` sous **licence MIT vérifiée par l'API GitHub** — pas
 * lue dans un README, le contrôle que le §1.1 exige nommément et qui avait déjà
 * été fait pour le jeu d'exercices. L'avis de copyright voyage avec le fichier,
 * parce que MIT l'**exige** (contrairement à l'Unlicense du jeu d'exercices).
 *
 * 🔴 **Ce que ce changement fait disparaître, et c'est tant mieux** : les douze
 * contrôles géométriques du banc du 29/08 (« tout dans le cadre, les formes
 * miroir à gauche de l'axe, chaque muscle dans une bande verticale
 * plausible »). Ils validaient un dessin *que j'écrivais* ; ils n'ont plus
 * d'objet sur une géométrie reprise telle quelle et déjà éprouvée ailleurs. La
 * symétrie par miroir disparaît avec eux — la source porte ses deux côtés.
 *
 * ⚠️ **Ce qui NE change pas** : la pondération (un muscle secondaire compte
 * pour moitié, `shared/muscles.ts`), l'intensité **relative** au muscle le plus
 * chargé, les quatre paliers, et le fait que la carte **situe sans mesurer**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🔴 **La liste des muscles sous la carte a été RETIRÉE**, remplacée par une
 * infobulle au toucher *(06/09/2026, demandé)*. Elle existait comme encodage
 * secondaire — « la couleur ne porte jamais seule l'information » — et cette
 * exigence est **tenue autrement** : le chiffre reste atteignable, sur le
 * muscle même, d'un seul toucher. C'est même plus juste que la liste, qui
 * obligeait à faire la correspondance entre un nom lu en bas et une tache vue
 * en haut.
 *
 * ⚠️ La **légende** des paliers, elle, reste : elle nomme ce que les teintes
 * veulent dire, et aucune infobulle ne remplace ça.
 */

import { useState } from 'react';

import { ChartTooltip } from '../../components/ui/ChartTooltip';
import { useElementWidth } from '../../hooks/useElementWidth';
import { DOS, FACE, type RegionCorps, VIEWBOX } from './bodyModel';
import type { MuscleLoad } from './muscleVolume';

/**
 * Les quatre paliers, du non travaillé au plus chargé.
 *
 * ⚠️ Le palier 0 est `--line`, la même teinte que les traits : un muscle qui ne
 * travaille pas fait partie du corps sans se signaler.
 */
const PALIERS = ['var(--line)', 'var(--brand-tint)', 'var(--brand)', 'var(--brand-dark)'];

/** Teinte des pièces qui ne portent aucun muscle — tête, genoux. */
const NEUTRE = 'var(--line)';

/**
 * Le rapport hauteur / largeur du modèle (cadre `0 0 1000 2200`).
 *
 * ⚠️ Dérivé du `viewBox`, jamais recopié : le jour où la source change de
 * cadre, un nombre écrit à la main mentirait sur la hauteur — et l'infobulle
 * s'ancrerait de travers sans que rien ne le dise.
 */
const RATIO_CORPS = (() => {
  const [, , largeur, hauteur] = VIEWBOX.split(' ').map(Number);
  return (hauteur ?? 1) / (largeur ?? 1);
})();

/**
 * 🔴 **Le contour d'une région suit son PALIER — une mesure, pas un goût**
 * *(31/08/2026, conservé tel quel)*.
 *
 * Sans trait, deux régions voisines de même intensité se fondent en une seule
 * tache. **Aucun trait unique ne convient**, et les chiffres le disent sans
 * appel (contraste du trait sur son propre remplissage) :
 *
 * | trait | sur `--line` | sur `--brand-tint` | sur `--brand` | sur `--brand-dark` |
 * |-|-|-|-|-|
 * | blanc | **1,22** | **1,11** | 2,58 | 4,08 |
 * | `--ink-soft` | 4,14 | 4,54 | **1,97** | **1,24** |
 *
 * Le blanc s'efface sur les deux remplissages clairs — très exactement là où la
 * délimitation manque le plus ; l'encre s'efface sur les deux verts. D'où deux
 * traits et une règle simple : encre sur les clairs, blanc sur les verts. Le
 * plus faible des quatre couples tient alors **2,58:1**.
 *
 * ⚠️ Ce n'est pas un encodage : le trait ne dit rien de l'intensité — c'est le
 * remplissage qui la porte. Il ne fait que séparer deux formes, donc le
 * plancher de 3:1 du non-textuel *signifiant* ne s'y applique pas. On mesure
 * quand même : **un trait qu'on ne voit pas ne sépare rien.**
 */
const TRAIT = ['var(--ink-soft)', 'var(--ink-soft)', 'var(--on-solid)', 'var(--on-solid)'];

interface Designation {
  vue: 'face' | 'dos';
  cle: string;
  x: number;
  y: number;
  /** Les muscles travaillés de la région, avec leur volume. */
  muscles: MuscleLoad[];
}

/** Les demi-séries viennent des muscles secondaires : elles s'écrivent telles quelles. */
const arrondi = (sets: number) => (Number.isInteger(sets) ? String(sets) : sets.toFixed(1));

function Silhouette({
  vue,
  regions,
  intensite,
  charges,
  titre,
  designation,
  onDesigner,
}: {
  vue: 'face' | 'dos';
  regions: readonly RegionCorps[];
  intensite: ReadonlyMap<string, number>;
  charges: ReadonlyMap<string, number>;
  titre: string;
  designation: Designation | null;
  onDesigner: (designation: Designation | null) => void;
}) {
  /*
   * 🔴 **Le cadre de l'infobulle est MESURÉ, jamais supposé.** Le premier jet
   * passait 200 × 400 en dur : `tooltipAnchor` s'en sert pour décider dans quel
   * tiers tombe le point et pour garantir que la bulle ne sort pas — deux
   * nombres inventés auraient rendu cette garantie fausse, et le débordement
   * n'aurait pas été signalé (`.n-card` porte `overflow: hidden`, donc une
   * bulle qui déborde est **découpée**, pas dénoncée).
   *
   * ⚠️ Et c'est bien le conteneur d'**une vue** qu'il faut : les deux se
   * partagent la largeur de la carte, donc une bulle ancrée sur la carte
   * entière sortirait de sa moitié.
   */
  const { ref, width } = useElementWidth();

  return (
    <div className="n-body__view">
      <div className="n-chart-host" ref={ref}>
        <svg
          className="n-body__svg"
          viewBox={VIEWBOX}
          role="img"
          aria-label={titre}
          preserveAspectRatio="xMidYMid meet"
        >
          {regions.map((region) => {
            /*
             * ⚠️ **Une région peut porter PLUSIEURS muscles** (le trapèze
             * couvre aussi le milieu du dos) : elle prend alors l'intensité
             * **maximale**. Prendre la moyenne ferait pâlir un trapèze bien
             * travaillé parce que le milieu du dos ne l'était pas.
             */
            const niveau = region.muscles.reduce(
              (haut, muscle) => Math.max(haut, intensite.get(muscle) ?? 0),
              0,
            );
            const travailles = region.muscles
              .map((muscle) => ({ muscle, sets: charges.get(muscle) ?? 0 }))
              .filter((load) => load.sets > 0);
            const cliquable = travailles.length > 0;
            const actif = designation?.vue === vue && designation.cle === region.cle;

            return (
              <g
                key={region.cle}
                /*
                 * ⚠️ **`pointer-events` retiré des pièces neutres et des muscles
                 * au repos** : un toucher qui n'ouvre rien doit traverser, sinon
                 * il *ferme* l'infobulle en cours sans rien dire — on croirait
                 * avoir raté la cible.
                 */
                style={{ cursor: cliquable ? 'pointer' : 'default' }}
                onPointerDown={
                  cliquable
                    ? (event) => {
                        const boite = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                        if (boite === undefined) return;
                        // Un second toucher sur la même région la referme.
                        if (actif) {
                          onDesigner(null);
                          return;
                        }
                        onDesigner({
                          vue,
                          cle: region.cle,
                          x: event.clientX - boite.left,
                          y: event.clientY - boite.top,
                          muscles: travailles,
                        });
                      }
                    : undefined
                }
              >
                {region.polygones.map((points, index) => (
                  <polygon
                    key={index}
                    points={points}
                    style={{
                      fill: region.muscles.length === 0 ? NEUTRE : PALIERS[niveau],
                      stroke: region.muscles.length === 0 ? TRAIT[0] : TRAIT[niveau],
                    }}
                    strokeWidth={2}
                    /*
                     * ⚠️ **Le trait ne maigrit pas avec la figure** : le cadre
                     * fait 1 000 unités pour une vue de ~150 px, donc un trait
                     * de 2 unités mesurerait un tiers de pixel. Une
                     * délimitation qui disparaît quand la place manque manque
                     * au moment où elle sert.
                     */
                    vectorEffect="non-scaling-stroke"
                    opacity={designation !== null && !actif ? 0.55 : 1}
                  />
                ))}
              </g>
            );
          })}
        </svg>

        {designation?.vue === vue && (
          <ChartTooltip
            x={designation.x}
            y={designation.y}
            /*
             * ⚠️ Le cadre de l'infobulle est celui du **conteneur**, mesuré à
             * l'exécution : les deux vues se partagent la largeur, donc une
             * bulle ancrée sur la largeur de la carte sortirait de sa moitié.
             */
            boxWidth={width}
            boxHeight={width * RATIO_CORPS}
          >
            {designation.muscles.map((load) => (
              <div key={load.muscle}>
                {load.muscle} · {arrondi(load.sets)}
                {load.sets > 1 ? ' séries' : ' série'}
              </div>
            ))}
          </ChartTooltip>
        )}
      </div>
      <p className="n-eyebrow">{vue === 'face' ? 'Face' : 'Dos'}</p>
    </div>
  );
}

/**
 * Les muscles que le modèle sait situer.
 *
 * ⚠️ **Non exportée** : `react-refresh` interdit à un module de composants
 * d'exporter autre chose — la règle qui a déjà sorti `setLabels.ts` et
 * `exercisePassage.ts` de leurs écrans.
 */
const MUSCLES_SITUES: ReadonlySet<string> = new Set(
  [...FACE, ...DOS].flatMap((region) => region.muscles),
);

export function BodyMap({
  loads,
  intensite,
}: {
  loads: readonly MuscleLoad[];
  intensite: ReadonlyMap<string, number>;
}) {
  const [designation, setDesignation] = useState<Designation | null>(null);

  if (loads.length === 0) return null;

  /*
   * ⚠️ **Les muscles qu'aucune région ne représente sont signalés**, pas
   * ignorés : un exercice perso peut nommer ses muscles autrement, et une carte
   * qui les tairait laisserait croire qu'ils ne travaillent pas.
   *
   * ⚠️ Depuis le modèle repris, les **dix-sept** muscles du référentiel sont
   * situés — cette note ne se déclenche donc plus que sur du vocabulaire
   * personnel. Elle reste : c'est précisément ce cas qu'elle existe pour dire.
   */
  const horsCarte = loads.filter((load) => !MUSCLES_SITUES.has(load.muscle));
  const charges = new Map(loads.map((load) => [load.muscle, load.sets]));

  return (
    <div className="n-body">
      <div className="n-body__views">
        {(
          [
            ['face', FACE, 'Muscles travaillés, vue de face'],
            ['dos', DOS, 'Muscles travaillés, vue de dos'],
          ] as const
        ).map(([vue, regions, titre]) => (
          <Silhouette
            key={vue}
            vue={vue}
            regions={regions}
            intensite={intensite}
            charges={charges}
            titre={titre}
            designation={designation}
            onDesigner={setDesignation}
          />
        ))}
      </div>

      {/*
        🟠 **La légende nomme les paliers** — la teinte seule ne dit rien de
        comparable, et trois verts sans libellé se lisent comme un dégradé
        décoratif. Elle survit au retrait de la liste : une infobulle donne un
        chiffre sur demande, elle ne dit pas ce que les couleurs veulent dire.
      */}
      <div className="n-body__legend">
        {['Peu', 'Moyen', 'Beaucoup'].map((label, i) => (
          <span key={label} className="n-body__key">
            <i style={{ background: PALIERS[i + 1] }} aria-hidden />
            {label}
          </span>
        ))}
      </div>

      <p className="n-hint" style={{ margin: 0, textAlign: 'center' }}>
        Touchez un muscle coloré pour voir son volume.
      </p>

      {horsCarte.length > 0 && (
        <p className="n-hint" style={{ margin: 0 }}>
          Non situés sur la carte : {horsCarte.map((load) => load.muscle).join(', ')}.
        </p>
      )}
    </div>
  );
}
